import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration } from '../../../data/configuration.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface ConsoleAndLowerRailViewProps {
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface ConsoleAndLowerRailViewContextType {
	selectedConsoleItemId: string;
	handleConsoleItemClicked: (id: string) => void;
	selectedGearStickItemId?: string;
	handleGearStickItemClicked?: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function ConsoleAndLowerRailView(props: ConsoleAndLowerRailViewProps) {
	if (Configuration.isSupersport400Model()) {
		return ConsoleAndLowerRailView400(props);
	}
	else {
		return ConsoleAndLowerRailViewStandard(props);
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function ConsoleAndLowerRailView400(props: ConsoleAndLowerRailViewProps) {

	// initial values.
	const initialConsoleItemId = Configuration.centreConsoleOption;
	const initialConsoleItemName = () => getConsoleItemName(initialConsoleItemId);
	const initialGearStickItemId = Configuration.gearStickOption;
	const initialGearStickItemName = () => getGearStickItemName(initialGearStickItemId);

	// states.
	const [selectedConsoleItemId, setSelectedConsoleItemId] = useState(initialConsoleItemId);
	const [selectedConsoleItemName, setSelectedConsoleItemName] = useState(initialConsoleItemName);
	const [selectedGearStickItemId, setSelectedGearStickItemId] = useState(initialGearStickItemId);
	const [selectedGearStickItemName, setSelectedGearStickItemName] = useState(initialGearStickItemName);

	// handlers.

	const handleConsoleItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedConsoleItemId(id);
		setSelectedConsoleItemName(getConsoleItemName(id));

		// update the configuration.
		Configuration.centreConsoleOption = id;
		Configuration.updateCosts();

		// select the centre console on the car.
		props.interiorConfigurator.selectCentreConsoleOption(id);
	};

	const handleGearStickItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedGearStickItemId(id);
		setSelectedGearStickItemName(getGearStickItemName(id));

		// update the configuration.
		Configuration.gearStickOption = id;
		Configuration.updateCosts();

		// select the gear stick on the car.
		props.interiorConfigurator.selectGearStickOption(id);
	}

	// return the HTML.
	return (
		<ConsoleAndLowerRailViewContext.Provider value={{ selectedConsoleItemId, handleConsoleItemClicked, selectedGearStickItemId, handleGearStickItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-console-and-lower-rail">CENTRE CONSOLE</h2>
					</div>

					<SectionPanel
						subHeading={'COCKPIT WOOD'}
						modalContent={CloudData.getModalContent(selectedConsoleItemId, CloudData.centreConsoleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedConsoleItemName}
							sectionPrice={CloudData.getPrice(selectedConsoleItemId, CloudData.centreConsoleOptions, { ref: false })}
						/>
						<ConsoleItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'GEAR SELECTOR'}
						modalContent={CloudData.getModalContent(selectedGearStickItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={selectedGearStickItemName}
							sectionPrice={CloudData.getPrice(selectedGearStickItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<GearStickItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</ConsoleAndLowerRailViewContext.Provider>
	);
}

function ConsoleAndLowerRailViewStandard(props: ConsoleAndLowerRailViewProps) {

	// initial values.
	const initialConsoleItemId = Configuration.centreConsoleOption;
	const initialConsoleItemName = () => getConsoleItemName(initialConsoleItemId);

	// states.
	const [selectedConsoleItemId, setSelectedConsoleItemId] = useState(initialConsoleItemId);
	const [selectedConsoleItemName, setSelectedConsoleItemName] = useState(initialConsoleItemName);

	// handlers.

	const handleConsoleItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedConsoleItemId(id);
		setSelectedConsoleItemName(getConsoleItemName(id));

		// update the configuration.
		Configuration.centreConsoleOption = id;
		Configuration.updateCosts();

		// select the centre console on the car.
		props.interiorConfigurator.selectCentreConsoleOption(id);
	};

	// return the HTML.
	return (
		<ConsoleAndLowerRailViewContext.Provider value={{ selectedConsoleItemId, handleConsoleItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-console-and-lower-rail">COCKPIT WOOD</h2>
					</div>

					<SectionPanel
						subHeading={'COCKPIT WOOD'}
						modalContent={CloudData.getModalContent(selectedConsoleItemId, CloudData.centreConsoleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedConsoleItemName}
							sectionPrice={CloudData.getPrice(selectedConsoleItemId, CloudData.centreConsoleOptions, { ref: false })}
						/>
						<ConsoleItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</ConsoleAndLowerRailViewContext.Provider>
	);
}

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const ConsoleAndLowerRailViewContext = createContext<ConsoleAndLowerRailViewContextType | undefined>(undefined);

function ConsoleItems() {
	return (
		<ViewContentRows maxRowLength={11}>
			<ConsoleItem id={'CW012'} imageFilePath={'ash-olive'} />
			<ConsoleItem id={'CW013'} imageFilePath={'rosewood-santos'} />
			<ConsoleItem id={'CW014'} imageFilePath={'oak-european-smoked'} />
			<ConsoleItem id={'CW015'} imageFilePath={'oak-american-smoked'} />
			<ConsoleItem id={'CW016'} imageFilePath={'blackened-ash'} />
			<ConsoleItem id={'CW017'} imageFilePath={'bog-oak-medullary'} />
			<ConsoleItem id={'CW005'} imageFilePath={'walnut'} />
			<ConsoleItem id={'CW008'} imageFilePath={'birch-and-aluminium'} />
			<ConsoleItem id={'CW009'} imageFilePath={'walnut-and-aluminium'} />
			<ConsoleItem id={'CW010'} imageFilePath={'kinetic-diamond'} />
			<ConsoleItem id={'CW011'} imageFilePath={'spokes-in-motion'} />
			{Configuration.isSupersport400Model() &&
				<ConsoleItem id={'CW018'} imageFilePath={'s400-veneer'} />
			}
		</ViewContentRows>
	);
}

function ConsoleItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ConsoleAndLowerRailViewContext);
	if (!context) {
		throw new Error('ConsoleAndLowerRailViewContext must be used within a ConsoleAndLowerRailView component');
	}
	
	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getConsoleItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/centre-console-and-rail/cockpit-wood/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedConsoleItemId}
			onItemClicked={context.handleConsoleItemClicked}
		/>
	);
}

function GearStickItems() {
	return (
		<ViewContentRows>
			<GearStickItem id={'GS001'} imageFilePath={'standard'} />
			<GearStickItem id={'GS002'} imageFilePath={'aluminium'} />
		</ViewContentRows>
	);
}

function GearStickItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(ConsoleAndLowerRailViewContext);
	if (!context) {
		throw new Error('ConsoleAndLowerRailViewContext must be used within a ConsoleAndLowerRailView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getGearStickItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/centre-console-and-rail/gear-stick/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedGearStickItemId}
			onItemClicked={context.handleGearStickItemClicked!}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getConsoleItemName(id: string): string {
	return CloudData.getName(id, CloudData.centreConsoleOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getGearStickItemName(id: string): string {
	return CloudData.getName(id, CloudData.accessoryOptions);
}